import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookPlan } from '@kitup/book-schema';
import { useState, type JSX } from 'react';
import { Link } from 'react-router';
import type { BookDto } from '../api/books.js';
import {
  previewImage,
  renditionFor,
  thingsApi,
  THING_CATEGORIES,
  type ThingCategory,
  type ThingDto,
} from '../api/library.js';
import { SelectField } from '../components/Fields.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { SimpleEditor } from '../library/SimpleEditor.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';
import { CharacterThumb } from './CharactersStep.js';

/**
 * Шаг «Любимые вещи» (E7-T3) — **опциональный**. Пустой список здесь ничего
 * не блокирует и не помечается ошибкой: книга без вещей собирается целиком.
 */
export function ThingsStep({
  book,
  readOnly = false,
}: {
  book: BookDto;
  readOnly?: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const action = useActionFeedback();

  const linked = useQuery({
    queryKey: ['book-things', book.id],
    queryFn: () => thingsApi.forBook(book.id),
  });
  const library = useQuery({
    queryKey: ['things'],
    queryFn: () => thingsApi.list(),
    enabled: !readOnly,
  });
  const items = linked.data?.items ?? [];
  const limit = bookPlan(book.plan).maxThings;
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    items.map((item) => item.id),
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['book-things', book.id] });
    void queryClient.invalidateQueries({ queryKey: ['things'] });
    void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  };
  const link = (ids: readonly string[]): Promise<void> =>
    thingsApi
      .linkToBook(
        book.id,
        ids.map((thingId, order) => ({ thingId, order })),
      )
      .then(refresh);

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t.things.stepTitle}</h1>
        <p className="mt-1 text-sm font-medium text-[var(--kitup-color-muted)]">
          {t.monetization.planLimit(items.length, limit)}
        </p>
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.things.stepSubtitle}</p>
        {/* The empty state below says the same while there is nothing here. */}
        {items.length > 0 ? (
          <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.things.optional}</p>
        ) : null}
      </div>

      {readOnly || items.length >= limit ? null : (
        <LibraryPicker
          addLabel={t.things.add}
          createLabel={t.things.create}
          nameLabel={t.things.fields.name}
          options={(library.data?.items ?? [])
            .filter((thing) => !items.some((item) => item.id === thing.id))
            .map((thing) => ({
              id: thing.id,
              label: thing.name,
              detail:
                thing.usage.books > 0
                  ? t.library.picker.usedIn(thing.usage.books)
                  : t.library.picker.notUsed,
            }))}
          onAdd={(id) => link([...items.map((item) => item.id), id])}
          onCreate={(name) =>
            thingsApi.create({ name, category: 'toy' }).then((created) => {
              setOpenId(created.id);
              scrollToCreatedItem(created.id);
              return link([...items.map((item) => item.id), created.id]);
            })
          }
        />
      )}
      <ActionError message={action.error} />

      {items.length === 0 ? <EmptyState title={t.things.stepEmpty} /> : null}

      <ul className="grid gap-3">
        {items.map((thing) => (
          <li ref={itemRef(thing.id)} key={thing.id} className="kitup-card p-4">
            <div className="kitup-expandable-row flex flex-wrap items-center justify-between gap-3">
              {/* The same row as a character's: the drawing, the name, and where it stands. */}
              <div className="flex items-center gap-3">
                <CharacterThumb url={previewImage(thing, book.styleId)} />
                <div className="grid gap-0.5">
                  <button
                    type="button"
                    className="kitup-expandable-trigger"
                    aria-expanded={openId === thing.id}
                    onClick={() => setOpenId(openId === thing.id ? null : thing.id)}
                  >
                    <span>{thing.name}</span>
                    <span className="kitup-expandable-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  <span className="text-xs text-[var(--kitup-color-muted)]">
                    {thingStatus(thing, book.styleId, t)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="text-sm text-red-700 underline-offset-2 hover:underline disabled:opacity-50"
                disabled={readOnly || action.pending}
                onClick={() =>
                  action.run(
                    link(items.filter((item) => item.id !== thing.id).map((item) => item.id)),
                  )
                }
              >
                {t.library.actions.removeFromBook}
              </button>
            </div>
            {openId === thing.id ? (
              <div className="mt-4">
                <SimpleEditor
                  entity={thing}
                  api={thingsApi}
                  styleId={book.styleId}
                  bookId={book.id}
                  readOnly={readOnly}
                  uploadKind="thing_ref"
                  maxPhotos={3}
                  photoHint={t.library.photos.hintThing}
                  showPrompt={false}
                  labels={{ name: t.things.fields.name, description: t.things.fields.description }}
                  kindField={
                    <SelectField
                      label={t.things.fields.category}
                      value={thing.category}
                      options={THING_CATEGORIES.map((category) => ({
                        value: category,
                        label: t.things.categories[category],
                      }))}
                      onChange={(category: ThingCategory) =>
                        action.run(thingsApi.update(thing.id, { category }).then(refresh))
                      }
                    />
                  }
                  footer={
                    <p className="mb-3 text-sm text-[var(--kitup-color-muted)]">
                      {t.things.noApprovalNeeded}
                    </p>
                  }
                  onChanged={refresh}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {readOnly ? null : (
        <Link
          to={`/app/things?bookId=${encodeURIComponent(book.id)}`}
          className="justify-self-start text-sm text-[var(--kitup-color-muted)] underline underline-offset-2"
        >
          {t.library.actions.openLibrary}
        </Link>
      )}
    </div>
  );
}

/** A thing needs no approval: once a drawing is chosen it is simply drawn. */
function thingStatus(thing: ThingDto, styleId: string, t: ReturnType<typeof useI18n>['t']): string {
  const rendition = renditionFor(thing, styleId);
  const statuses = t.library.rendition.statuses;
  if (rendition?.status === 'generating') return statuses.generating;
  // The server picks the latest variant when none is chosen; either way, it is drawn.
  if (rendition?.selectedVariantId || (rendition?.variants.length ?? 0) > 0) return t.things.drawn;
  if (rendition?.status === 'failed') return statuses.failed;
  return statuses.empty;
}
